<template>
  <div class="magazine-article" data-track-as="magazine">
    <header-image
      v-if="headerImage"
      :image="headerImageObject"
      class="magazine-article-header-image"
    />
    <div class="content-area">
      <div class="content-column">
        <magazine-registration
          class="magazine-article__registration"
          :registration-form-submit-url="registrationFormSubmitUrl"
          :registration-form-formstart="registrationFormFormstart"
          :registration-form-formid="registrationFormFormid"
          :social-links="socialLinks"
        />
        <slot></slot>
      </div>
    </div>
  </div>
</template>

<script>
import HeaderImage from '@/components/HeaderImage'

import MagazineRegistration from '../common/MagazineRegistration'

export default {
  name: 'SanitasMagazineArticle',

  components: {
    MagazineRegistration,
    HeaderImage,
  },

  props: {
    headerImageRatio: {
      type: [Number, String],
      default: 0.5625,
    },
    headerImage: {
      type: String,
      default: '',
    },
    headerImageSrcset: {
      type: String,
      default: '',
    },
    registrationFormSubmitUrl: {
      type: String,
      default: '',
    },
    registrationFormFormid: {
      type: String,
      default: '',
    },
    registrationFormFormstart: {
      type: String,
      default: '',
    },
    socialLinks: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      socialMagazineVisible: false,
    }
  },

  computed: {
    headerImageObject() {
      return {
        src: this.headerImage,
        srcSet: this.headerImageSrcset,
        ratio: this.headerImageRatio,
      }
    },
    sources() {
      const sourceSet = this.headerImageSrcset.split(',')
      const sources = []
      sourceSet.forEach(set => {
        const source = set.trim().split(' ')
        const size = source[1].match('(.*)w')
        if (size && size[1]) {
          sources.unshift({
            image: source[0],
            size: `(min-width: ${size[1]}px)`,
          })
        }
      })
      return sources
    },
  },

  beforeMount() {
    /* To avoid div's automatically inserted by AEM from having effects on the frontend CSS or markup structure,
       we strip away all div.parbase tags during initialization of the top-level magazine article component.
    */
    function stripParbase(inArray) {
      let outArray = []
      inArray.forEach(element => {
        if (
          element.tag === 'div' &&
          element.data &&
          element.data.staticClass &&
          element.data.staticClass.match(/\bparbase\b/)
        ) {
          if (element.children) {
            outArray.push(...element.children)
          }
        } else {
          outArray.push(element)
        }
      })
      outArray.forEach(element => {
        if (element.children) {
          element.children = stripParbase(element.children)
        }
        if (element.componentOptions && element.componentOptions.children) {
          element.componentOptions.children = stripParbase(
            element.componentOptions.children,
          )
        }
      })
      return outArray
    }

    /* In AEM, there is an element "Section separator" that renders an empty div. If we encounter
       such a div within an article tag, we render section tags accordingly, while ignoring the
       initial title, lead and author tags
    */
    function splitSections(inArray, createElement) {
      inArray.forEach(element => {
        if (
          element.tag === 'article' &&
          element.children &&
          element.children.length
        ) {
          let inMainBody = false
          let createSections = false
          let newChildren = []
          let nextChildren = []
          element.children.forEach(element => {
            if (
              element.tag === 'div' &&
              element.data &&
              element.data.staticClass &&
              element.data.staticClass.match(
                /\bsan-editorial-sectionseparator\b/,
              )
            ) {
              inMainBody = false
              createSections = true
              if (nextChildren.length) {
                newChildren.push(createElement('section', nextChildren))
                nextChildren = []
              }
            } else if (!inMainBody) {
              if (
                !element.tag ||
                element.tag === 'h1' ||
                (element.data &&
                  element.data.staticClass &&
                  element.data.staticClass.match(/\b(lead|author)\b/))
              ) {
                newChildren.push(element)
              } else {
                inMainBody = true
                nextChildren.push(element)
              }
            } else {
              nextChildren.push(element)
            }
          })
          if (nextChildren.length) {
            if (createSections) {
              newChildren.push(createElement('section', nextChildren))
            } else {
              newChildren.push(...nextChildren)
            }
          }
          element.children = newChildren
        }
      })
    }

    if (this.$slots.default && this.$slots.default.length) {
      this.$slots.default = stripParbase(this.$slots.default)
      splitSections(this.$slots.default, this.$createElement)
    }
  },
}
</script>

<style lang="postcss">
/* Base element styling */
.magazine-article sup {
  top: -0.2em !important;
}

.magazine-article .content-area {
  --magazine-article-content-width-default: 800px;

  h1 {
    font-family: 'Faustina', serif;
    font-weight: 700;
    font-size: 60px;
    line-height: 70px;
    margin-bottom: 40px;
    color: var(--c-primary-neutral-1);
    overflow-wrap: break-word;
  }

  h3 {
    font-family: 'Faustina', serif;
  }

  h2,
  h4 {
    /* Overwrite global h4 font family TheMix Bold */
    font-family: 'Open Sans', sans-serif;
    font-size: 20px;
    font-weight: 700;
    line-height: 1.5;
    color: inherit;
  }

  p {
    font-family: 'Faustina', serif;
    font-weight: 400;
    font-size: 20px;
    line-height: 1.5;
    margin-bottom: 30px;
    color: var(--c-primary-neutral-1);

    a {
      border-bottom: none;
      font-family: inherit;
      font-size: inherit;
      display: inline-flex;

      .dotcom-icon {
        left: -24px;
      }

      &:hover {
        color: var(--c-primary-color-3);
        border-bottom: 1px solid var(--c-primary-color-3);
      }
    }
  }

  dl {
    font-size: 20px;
    line-height: 1.5;
    margin-top: 0;
    margin-bottom: 40px;

    dd {
      font-size: 20px;
      font-weight: 700;
      line-height: 1.5;
      margin-left: 0;
      color: var(--c-primary-neutral-1);
    }

    dt + dd {
      margin-top: 40px;
    }

    dt {
      font-family: 'Faustina', serif;
    }
  }

  /* TODO: remove duplicated styles in MagazineQuote */

  blockquote {
    font-family: 'Faustina', serif;
    font-weight: 700;
    font-size: 24px;
    line-height: 1.46;
    text-align: center;
    margin-bottom: 40px;

    footer {
      font-weight: 400;
      font-size: 15px;
      margin-top: 14px;
    }

    &:before {
      content: '';
      display: block;
      margin: 0 auto 40px;
      width: 160px;
      border: 1px solid var(--c-primary-neutral-1);
    }

    &:after {
      content: '';
      display: block;
      margin: 40px auto 0;
      width: 160px;
      border: 1px solid var(--c-primary-neutral-1);
    }
  }

  h2 + ul {
    margin-top: 20px;
  }

  ul {
    margin-left: 0;
    font-weight: 400;
    font-size: 20px;
    line-height: 1.5;
    margin-bottom: 40px;
    color: var(--c-primary-neutral-1);
    overflow-wrap: break-word;
    list-style: disc;
    padding-left: 21px;
    list-style-position: outside;
  }

  li {
    font-family: 'Faustina', serif;
    font-size: 20px;
    background: none;
    line-height: 27px;
    text-align: left;
    padding-left: 0;
    padding-bottom: 5px;
    color: var(--c-primary-neutral-1);

    b {
      font-weight: 700;
      color: var(--c-primary-neutral-1);
      line-height: 30px;
    }
  }

  ol {
    padding-left: 18px;
    list-style-position: outside;

    li {
      list-style-type: inherit;
      text-indent: 0;

      &:before {
        content: none;
      }
    }
  }

  sup {
    top: -0.2em !important;
  }
}

.magazine-article .content-area .san-richtext {
  margin-bottom: 40px;
}

.magazine-article__registration .magazine-registration__link-list {
  padding-top: var(--spacing-4);
  padding-bottom: 0;
}

@media (max-width: 490px) {
  .magazine-article .content-area {
    h1 {
      font-size: 32px;
      line-height: 35px;
    }

    h2 {
      font-size: 15px;
    }

    p {
      font-size: 15px;
    }

    dl,
    dl dd {
      font-size: 15px;
    }

    ol {
      padding-left: 14px;
    }
  }

  .magazine-article .content-area .san-richtext {
    li {
      font-size: 15px;
    }
  }
}

/* Styles based on classes */
.magazine-article {
  background-color: var(--c-primary-neutral-2);

  margin-left: calc(-1 * var(--body-offset-horizontal));
  margin-right: calc(-1 * var(--body-offset-horizontal));
  margin-bottom: calc(-1 * var(--spacing-16));

  #prefixbox,
  #suffixbox {
    padding: 0 !important;
    width: auto !important;
  }

  .content-area {
    background-color: white;
    max-width: 1100px;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    color: var(--c-primary-neutral-1);

    .content-column {
      width: 100%;

      & > *:not(article),
      & > article > *:not(section),
      & section > * {
        width: 100%;
        margin-left: auto;
        margin-right: auto;
        padding-left: var(--body-offset-horizontal);
        padding-right: var(--body-offset-horizontal);
      }

      .accordion {
        margin-bottom: 40px;
      }
    }

    .taglabel.header {
      font-size: 15px;
      font-weight: bold;
      line-height: normal;
      color: var(--c-primary-color-2);
      display: table;
      margin-top: 10px;

      &:hover {
        color: var(--c-primary-color-3);
        border-bottom: none;
      }
    }

    .taglabel.header + h1 {
      line-height: 1.1;
    }

    .lead {
      font-size: 24px;
      line-height: 1.38;
      margin-bottom: 40px;
    }

    .author {
      font-size: 12px;
      color: var(--c-primary-neutral-1);
      margin-bottom: 80px;
    }
  }
}

.magazine-article-header-image + .content-area {
  margin-top: -200px;
}

@media (max-width: 1200px) {
  .magazine-article {
    margin-left: calc(-1 * var(--body-offset-horizontal));
    margin-right: calc(-1 * var(--body-offset-horizontal));
  }
}

@media (max-width: 1100px) {
  .magazine-article-header-image + .content-area {
    margin-top: 0;
  }
}

@media (max-width: 490px) {
  .magazine-article {
    margin-left: calc(-1 * var(--body-offset-horizontal));
    margin-right: calc(-1 * var(--body-offset-horizontal));

    .content-area {
      .lead {
        font-size: 20px;
        line-height: 1.5;
      }

      div.author {
        font-size: 12px;
      }
    }
  }
}

@media (min-width: 860px) {
  .magazine-article .content-area .content-column {
    & > *:not(article),
    & > article > *:not(section),
    & section > * {
      width: var(--magazine-article-content-width-default);
      padding-left: 0;
      padding-right: 0;
    }
  }
}

@media (--xs) {
  .magazine-article__registration .magazine-registration__link-list {
    padding-top: var(--spacing-6);
  }
}

@media (--md) {
  .magazine-article__registration .magazine-registration__link-list {
    position: absolute;
    right: var(--body-offset-horizontal);
  }
}
</style>

<style scoped>
.magazine-article .content-area .content-column .magazine-article-breadcrumb {
  min-height: 0;

  >>> ul {
    margin-bottom: 0;
    font-size: 12px;
  }
}

@media (--xs) {
  .magazine-article .content-area .content-column .magazine-article-breadcrumb {
    >>> ul {
      font-size: 16px;
    }
  }
}

@media (--md) {
  .magazine-article .content-area .content-column .magazine-article-breadcrumb {
    min-height: 80px;

    >>> .dotcom_breadcrumb {
      padding: var(--spacing-6) 0;
    }
  }
}

@media (min-width: 860px) {
  .magazine-article .content-area .content-column .magazine-article-breadcrumb {
    margin-left: var(--body-offset-horizontal);
  }
}
</style>
